// ============================================================================
// useUsageVisibility — the single client gate for usage/billing visibility
// (ORG-USAGE-2026)
//
// Wraps useRbac with the fail-open rules in usageVisibility.ts. Every surface
// that shows model/token/cost detail should ask this hook rather than calling
// hasPermission directly, so the grandfathering behavior stays consistent and
// there is exactly one place to reason about "could this hide something a user
// can see today?".
// ============================================================================

import { useMemo } from 'react';
import { useRbac } from './useRbac';
import { resolveUsageVisibility, UsageVisibility } from './usageVisibility';

export function useUsageVisibility(): UsageVisibility & {
  refetch: () => void;
  loading: boolean;
  error: string | null;
  isOwner: boolean;
} {
  const { hasPermission, isOwner, loading, error, usageVisibilityGeneration, hasIdentity, refetch } =
    useRbac();

  const visibility = useMemo(
    () =>
      resolveUsageVisibility({
        loading,
        error,
        isOwner,
        hasPermission,
        usageVisibilityGeneration,
        hasIdentity,
      }),
    [loading, error, isOwner, hasPermission, usageVisibilityGeneration, hasIdentity]
  );

  return { ...visibility, refetch, loading, error, isOwner };
}
